import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import preflightApiRequest from 'src/api/preflightApiRequest';
import { opApiRequest } from 'src/api/apiRequest';
import sendPostMessage from 'src/store/app/operation/sendPostMessage';
import { MESSAGE_TYPE } from 'src/store/app/types';
import { Store } from 'src/store/index';
import findCustomerPictureId from 'src/util/embroidery/findCustomerPictureId';

interface DigitizeTextPayload {
  textElement: any;
  path: string;
  output_px_width: number;
  elementUuid: number;
  variantKey: string | undefined;
}

interface DigitizeImagePayload {
  imageUrl: string;
  colorOverrides?: Record<string, string>;
  quantized_colors?: string[];
  rendered_mm_width: number;
  output_px_width: number;
  elementUuid: number;
  variantKey: string | undefined;
  palette_override?: string[];
  embroideryScale?: number;
}

interface DigitizationRequest {
  elementType: 'text' | 'image' | 'addon';
  elementId: string;
  productId?: string;
  imageId: string | undefined;
  reuseIfExist: boolean;
  operationType?: 'create' | 'update';
  payload: DigitizeTextPayload | DigitizeImagePayload;
}

interface DigitizeResponse {
  url: string;
  id: string;
  margin: number;
  stitch_count: number;
}

interface OpDigitizeResponse {
  url: string;
  id: string;
  stitchCount?: number;
}

const digitizeText = async (
  request: DigitizationRequest,
): Promise<DigitizeResponse> => {
  const payload = request.payload as DigitizeTextPayload;
  const params = new URLSearchParams({
    output_px_width: String(payload.output_px_width),
    image_id: request.imageId || '',
    element_hash: request.elementId || '',
  });
  if (request.reuseIfExist) {
    params.append('reuse_if_exist', 'true');
  }

  return preflightApiRequest(
    `/embroidery/text/digitize?${params.toString()}`,
    { text_element: payload.textElement, path: payload.path },
    'data',
    'POST',
  ) as Promise<DigitizeResponse>;
};

const digitizeImageViaOp = async (
  request: DigitizationRequest,
  customerPictureId: string,
): Promise<DigitizeResponse> => {
  const payload = request.payload as DigitizeImagePayload;

  const response = await opApiRequest(
    '/api/customer/gallery/v1/embroidery/image/digitize',
    {
      customerPictureId: Number(customerPictureId),
      renderedMmWidth: payload.rendered_mm_width,
      outputPxWidth: payload.output_px_width,
      returnStitchCount: true,
    },
    'data',
    'POST',
  ) as OpDigitizeResponse;

  return {
    url: response.url,
    id: response.id,
    margin: 0,
    stitch_count: response.stitchCount || 0,
  };
};

const digitizeImageViaPreFlight = async (
  request: DigitizationRequest,
): Promise<DigitizeResponse> => {
  const payload = request.payload as DigitizeImagePayload;
  const params = new URLSearchParams({
    image_url: payload.imageUrl,
    rendered_mm_width: String(payload.rendered_mm_width),
    output_px_width: String(payload.output_px_width),
    image_id: request.imageId || '',
    element_hash: request.elementId || '',
  });
  if (request.reuseIfExist) {
    params.append('reuse_if_exist', 'true');
  }

  const body: Record<string, any> = {
    replace: payload.colorOverrides || {},
    quantized_colors: payload.quantized_colors || [],
  };
  if (payload.palette_override) {
    body.palette_override = payload.palette_override;
  }

  return preflightApiRequest(
    `/embroidery/image/digitize?${params.toString()}`,
    body,
    'data',
    'POST',
  ) as Promise<DigitizeResponse>;
};

const handleDigitizationRequestOperation = (requestData: DigitizationRequest) => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  getState: () => Store,
) => {
  try {
    let response: DigitizeResponse;

    if (requestData.elementType === 'text') {
      response = await digitizeText(requestData);
    } else {
      const payload = requestData.payload as DigitizeImagePayload;
      const hasPaletteOverride = payload.palette_override && payload.palette_override.length > 0;
      const customerPictureId = findCustomerPictureId(getState(), requestData.imageId || '');

      // After recolor, editor sends palette_override with the new thread colors.
      // Use PreFlight direct path which passes palette_override to render correct
      // stitch preview. OP API doesn't support palette_override yet.
      if (customerPictureId && !hasPaletteOverride) {
        response = await digitizeImageViaOp(requestData, customerPictureId);
      } else {
        response = await digitizeImageViaPreFlight(requestData);
      }
    }

    sendPostMessage({
      type: MESSAGE_TYPE['design.setDigitizedAsset'],
      data: {
        url: response.url,
        elementId: requestData.elementId,
        imageId: response.id,
        stitchCount: response.stitch_count,
        margin: response.margin || 0,
        payload: requestData.payload,
        operationType: requestData.operationType,
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[OP-39631] Digitization failed:', error);

    sendPostMessage({
      type: MESSAGE_TYPE['design.setDigitizedAssetError'],
      data: {
        elementId: requestData.elementId,
        error: String(error),
        payload: requestData.payload,
        operationType: requestData.operationType,
      },
    });
  }
};

export default handleDigitizationRequestOperation;
